'use client'

import { Tooltip } from '~/components/component-library'
import { NetworkLogoView } from '~/components/component-library/NetworkLogo/components/NetworkLogoView'
import { NetworkLogoProps } from '~/components/component-library/NetworkLogo/types'
import { sizeMap } from '~/components/component-library/NetworkLogo/util'
import { SupportedVmTypes } from '~/constants/chains'
import { useRelayNetworks } from '~/hooks/useRelayNetworks'
import { sortChainIds } from '~/utils'
import { getChainLabel } from '~/utils/chainLabels'
import { captureException } from '~/utils/errors'
import { cn } from '~/utils/tailwind-utils'

export { NetworkLogoView }

export type {
  NetworkLogoProps,
  NetworkLogoViewProps,
} from '~/components/component-library/NetworkLogo/types'

export const NetworkLogo = ({
  chainId,
  chainIds = [],
  size,
  limit,
  className,
}: NetworkLogoProps) => {
  // Resolve relay metadata once for the whole list so the per-chain view stays
  // presentational. This is what lets unsupported chains (e.g. BNB) render their
  // logo instead of "?" — a per-chain lookup can't be a hook inside the map.
  const { data: relayNetworks } = useRelayNetworks({
    enabled: true,
    vmTypes: SupportedVmTypes,
  })
  const resolvedSize = (size ?? 'md') as keyof typeof sizeMap

  if (chainId && chainIds.length) {
    captureException(
      new Error(
        `Using both chainId and chainIds in NetworkLogo is not allowed.`,
      ),
    )
  }

  if (chainId) {
    return (
      <NetworkLogoView
        variant="standalone"
        chainId={chainId}
        size={resolvedSize}
        relayNetworkInfo={relayNetworks?.find(
          (network) => network.id === chainId,
        )}
        className={className}
      />
    )
  }

  const expandable = limit === undefined

  const sortedChainIds = sortChainIds(chainIds)
  const multiContainerClassName = cn(
    'flex flex-shrink-0 flex-row items-center space-x-1',
    { 'group hover:space-x-2': expandable },
    className,
  )

  return (
    <div className={multiContainerClassName}>
      <div
        className={cn('flex items-center -space-x-1.5 transition-all', {
          'group-hover:space-x-2': expandable,
        })}
      >
        {(expandable ? sortedChainIds : sortedChainIds.slice(0, limit)).map(
          (currentChainId, index) => {
            const zIndex = chainIds.length - index

            return (
              <div style={{ zIndex }} key={`${currentChainId}-${index}`}>
                <NetworkLogoView
                  variant="stacked"
                  chainId={currentChainId}
                  size={resolvedSize}
                  relayNetworkInfo={relayNetworks?.find(
                    (network) => network.id === currentChainId,
                  )}
                />
              </div>
            )
          },
        )}
      </div>
      {limit && chainIds.length > limit && !expandable && (
        <Tooltip
          content={
            <div>
              {sortedChainIds.slice(limit).map((id) => (
                <div key={id}>
                  {getChainLabel(id, {
                    relayNetwork: relayNetworks?.find(
                      (network) => network.id === id,
                    ),
                  })}
                </div>
              ))}
            </div>
          }
        >
          <div className="text-xs">+{chainIds.length - limit}</div>
        </Tooltip>
      )}
    </div>
  )
}
